/**
 * H.264 Annex-B helpers for the WebCodecs decoder.
 *
 * scrcpy v3.3.4 streams H.264 in Annex-B form (`00 00 00 01` start codes).
 * WebCodecs.VideoDecoder accepts either Annex-B (codec-string only) or
 * AVCC + avcC `description`. We choose the AVCC path because Safari
 * requires `description`. See:
 *   https://w3c.github.io/webcodecs/#dom-videodecoderconfig-description
 */

/** NAL unit types we care about. */
const NAL_SPS = 7;
const NAL_PPS = 8;

export interface ParsedSpsPps {
  /** Raw SPS NAL bytes (without start code). */
  sps: Uint8Array;
  /** Raw PPS NAL bytes (without start code). */
  pps: Uint8Array;
  /** profile_idc (byte 1 of SPS payload). */
  profileIdc: number;
  /** constraint_set_flags (byte 2 of SPS payload). */
  constraints: number;
  /** level_idc (byte 3 of SPS payload). */
  levelIdc: number;
}

/**
 * Iterate NAL units in an Annex-B buffer. Yields each NAL's payload bytes
 * (the byte after the start code through the next start code or EOF).
 */
function* iterAnnexB(buf: Uint8Array): Generator<Uint8Array> {
  const starts: number[] = [];
  for (let i = 0; i + 3 < buf.length; ) {
    if (buf[i] === 0 && buf[i + 1] === 0 && buf[i + 2] === 1) {
      starts.push(i + 3);
      i += 3;
    } else if (
      buf[i] === 0 &&
      buf[i + 1] === 0 &&
      buf[i + 2] === 0 &&
      buf[i + 3] === 1
    ) {
      starts.push(i + 4);
      i += 4;
    } else {
      i++;
    }
  }
  if (starts.length === 0) return;
  for (let k = 0; k < starts.length; k++) {
    const start = starts[k];
    const end = k + 1 < starts.length ? starts[k + 1] - 3 : buf.length;
    // Handle the rare case of a 4-byte start code preceding the next NAL.
    let realEnd = end;
    if (realEnd > 0 && buf[realEnd - 1] === 0) realEnd--;
    yield buf.subarray(start, realEnd);
  }
}

/** Parse SPS+PPS out of an Annex-B "config" payload from scrcpy. */
export function parseSpsPpsFromAnnexB(buf: Uint8Array): ParsedSpsPps | null {
  let sps: Uint8Array | null = null;
  let pps: Uint8Array | null = null;
  for (const nal of iterAnnexB(buf)) {
    if (nal.length === 0) continue;
    const type = nal[0] & 0x1f;
    if (type === NAL_SPS) sps = nal;
    else if (type === NAL_PPS) pps = nal;
  }
  if (!sps || sps.length < 4 || !pps) return null;
  return {
    sps,
    pps,
    profileIdc: sps[1],
    constraints: sps[2],
    levelIdc: sps[3],
  };
}

/** Hex helper: 2-char lowercase pad. */
const hex2 = (n: number): string => n.toString(16).padStart(2, '0');

/** Build the WebCodecs `codec` string `avc1.PPCCLL` from a parsed SPS. */
export function codecStringFromSps(p: ParsedSpsPps): string {
  return `avc1.${hex2(p.profileIdc)}${hex2(p.constraints)}${hex2(p.levelIdc)}`;
}

/**
 * Build an AVCDecoderConfigurationRecord ("avcC" box) from one SPS+PPS.
 * This is what `VideoDecoder.configure({ description })` consumes.
 *
 * Layout (ISO/IEC 14496-15 §5.2.4.1):
 *   u8  configurationVersion = 1
 *   u8  AVCProfileIndication = profile_idc
 *   u8  profile_compatibility = constraint_set_flags
 *   u8  AVCLevelIndication = level_idc
 *   u8  0xff                          // 6 bits reserved + 2 bits lengthSizeMinusOne (=3 → 4-byte)
 *   u8  0xe1                          // 3 bits reserved + 5 bits numOfSPS (=1)
 *   u16 spsLength
 *   u8[spsLength] sps
 *   u8  numOfPPS = 1
 *   u16 ppsLength
 *   u8[ppsLength] pps
 */
export function buildAvcCDescription(p: ParsedSpsPps): Uint8Array {
  const out = new Uint8Array(11 + p.sps.length + p.pps.length);
  let o = 0;
  out[o++] = 0x01;
  out[o++] = p.profileIdc;
  out[o++] = p.constraints;
  out[o++] = p.levelIdc;
  out[o++] = 0xff;
  out[o++] = 0xe1;
  out[o++] = (p.sps.length >> 8) & 0xff;
  out[o++] = p.sps.length & 0xff;
  out.set(p.sps, o);
  o += p.sps.length;
  out[o++] = 0x01;
  out[o++] = (p.pps.length >> 8) & 0xff;
  out[o++] = p.pps.length & 0xff;
  out.set(p.pps, o);
  return out;
}

/**
 * Convert Annex-B bytes (every NAL prefixed by `00 00 00 01` or `00 00 01`)
 * to AVCC bytes (every NAL prefixed by a 4-byte big-endian length).
 *
 * scrcpy frames may contain multiple NALs per packet (slice + auxiliary).
 */
export function annexBtoAvcc(buf: Uint8Array): Uint8Array {
  const nals: Uint8Array[] = [];
  for (const n of iterAnnexB(buf)) nals.push(n);
  if (nals.length === 0) return new Uint8Array(0);
  let total = 0;
  for (const n of nals) total += 4 + n.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const n of nals) {
    out[o++] = (n.length >>> 24) & 0xff;
    out[o++] = (n.length >>> 16) & 0xff;
    out[o++] = (n.length >>> 8) & 0xff;
    out[o++] = n.length & 0xff;
    out.set(n, o);
    o += n.length;
  }
  return out;
}
